import React, { MouseEventHandler, useCallback, useMemo, useRef } from "react";
import { NextSeo } from "next-seo";
import {
  VECTORIZATION_COLOR_MODE_LABELS,
  VECTORIZATION_MODE_LABELS,
} from "domains/assets/constants/vectorize";
import useAssetVectorize from "domains/assets/hooks/useAssetVectorize";
import { useAssetVectorizeCu } from "domains/assets/hooks/useAssetVectorizeCu";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import { FmImageListActionsProps } from "domains/file-manager/components/FileManagerImage/List/Actions";
import MoreTooltip from "domains/file-manager/components/FileManagerImage/List/MoreTooltip";
import ReuseButton from "domains/file-manager/components/FileManagerImage/List/ReuseButton";
import { FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS } from "domains/file-manager/constants/AssetFilter";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import ButtonSectionTopIcon from "domains/inference/components/InferenceGenerator/Sidebar/ButtonSectionTopIcon";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import SectionReferenceImageBase from "domains/inference/components/InferenceGenerator/Sidebar/SectionReferenceImageBase";
import { FM_HEADER_TOP_0 } from "domains/inference/constants/fileManagerStyle";
import {
  SectionsProvider,
  useSectionsContext,
} from "domains/inference/contexts/SectionsProvider";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { LaunchShortcut } from "domains/shortcuts/components/Shortcut";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import ButtonWithCuIndicator from "domains/ui/components/ButtonWithCuIndicator";
import Divider from "domains/ui/components/Divider";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";
import VectorizeParams from "domains/ui/components/VectorizeParams";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

export default function ImageVectorizePage() {
  return (
    <>
      <NextSeo title="Vectorize" />

      <SectionsProvider
        defaultCollapsedSections={[
          "prompt",
          "negativePrompt",
          "advancedSettings",
        ]}
      >
        <JobSessionProvider
          forcedFilterTypeOptions={[
            ...FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS,
          ]}
        >
          <ImageVectorize />
        </JobSessionProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

export function ImageVectorize() {
  const { infoToast } = useScenarioToast();
  const { openSection } = useSectionsContext();
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const {
    form,
    setValue,
    setReference,
    handleVectorizeJob,
    isVectorizeLoading,
    resetForm,
  } = useAssetVectorize();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { isCuLoading, cuCost } = useAssetVectorizeCu(form.reference?.id, form);

  const isVectorizeButtonDisabled = !form.reference?.id;

  const placeholders = useGetFileImagePlaceholders({
    length: 1,
    ...(form.reference?.metadata.width && form.reference?.metadata.height
      ? {
          dimensions: {
            width: form.reference.metadata.width,
            height: form.reference.metadata.height,
          },
        }
      : {}),
  });

  // ----------------------------------

  const handleMoreClick = useCallback(
    (key: string, value: any) => {
      switch (key) {
        case "colorMode":
        case "filterSpeckle":
        case "colorPrecision":
        case "layerDifference":
        case "mode":
        case "cornerThreshold":
        case "lengthThreshold":
        case "spliceThreshold":
          setValue(key, value);
          openSection("settings");
          break;
      }

      infoToast({
        title: "Settings updated",
      });
    },
    [infoToast, setValue, openSection]
  );

  const handleRefineClick = useCallback<
    CardActionsCustomProps["onRefineClick"]
  >(
    async (asset) => {
      await setReference(asset);

      infoToast({
        title: "Settings updated",
      });
    },
    [setReference, infoToast]
  );

  // ----------------------------------

  const vectorizeImage = useCallback(async () => {
    if (isVectorizeButtonDisabled) {
      return;
    }
    const job = await handleVectorizeJob({
      trackingExtraParams: {
        origin: "vectorize page",
      },
    });
    if (job) addJob(job);
  }, [handleVectorizeJob, addJob, isVectorizeButtonDisabled]);

  // ----------------------------------

  const isColorMode = form.colorMode === "color";
  const isSplineMode = form.mode === "spline";
  const listActionsProps = useMemo(
    () => ({
      isColorMode,
      isSplineMode,
      onMoreClick: handleMoreClick,
    }),
    [isColorMode, isSplineMode, handleMoreClick]
  );
  const cardActionsProps = useMemo(
    () => ({
      onRefineClick: handleRefineClick,
    }),
    [handleRefineClick]
  );

  // ----------------------------------

  return (
    <GeneratorContainer onSubmit={vectorizeImage}>
      <GeneratorSidebar>
        <SidebarTitle title="Vectorize" guide="vectorize-intro" />
        <Divider />

        <SectionReferenceImageBase
          id="referenceImage"
          title="Input Image"
          asset={form.reference}
          setAsset={setReference}
          removeBackgroundTrackingExtraParams={{
            origin: "vectorize page",
          }}
          selectLibrary={{
            tabs: ["image"],
          }}
        />
        <Divider />

        <SidebarSection>
          <ButtonWithCuIndicator
            onClick={vectorizeImage}
            isLoading={isVectorizeLoading}
            isDisabled={isVectorizeButtonDisabled}
            w={"100%"}
            cuCost={cuCost}
            isCuLoading={isCuLoading}
            data-testid="vectorize-action-button"
            tooltip={!isVectorizeButtonDisabled && <LaunchShortcut />}
            tooltipProps={{
              placement: "right",
              hasArrow: true,
              isDisabled: isVectorizeButtonDisabled,
            }}
          >
            Vectorize
          </ButtonWithCuIndicator>
        </SidebarSection>
        <Divider />

        <SidebarSection
          headerProps={{
            title: "Settings",
            id: "settings",
            topRight: (
              <ButtonSectionTopIcon onClick={resetForm}>
                <Icon id="Ui/Refresh" h="14px" />
              </ButtonSectionTopIcon>
            ),
          }}
        >
          <VectorizeParams form={form} setValue={setValue} />
        </SidebarSection>
        <Divider />
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <FileManagerImage
          scrollRef={scrollRef}
          files={
            !files?.length && !jobs?.length && !isLoading ? placeholders : files
          }
          jobs={jobs}
          isSelectable
          isLoading={isLoading}
          ListActionsComponent={ListActions}
          listActionsProps={listActionsProps}
          CardActionsComponent={CardActions}
          cardActionsProps={cardActionsProps}
          {...fileManagerProps}
          styleProps={FM_HEADER_TOP_0}
        />
      </GeneratorContent>
    </GeneratorContainer>
  );
}

// ------------------------------------

export type CardActionsCustomProps = {
  onRefineClick: (asset: GetAssetsByAssetIdApiResponse["asset"]) => void;
};

export type CardActionsProps = FmImageCardActionsProps<CardActionsCustomProps>;

function CardActions({ asset, top, bottom, onRefineClick }: CardActionsProps) {
  const canReuseImage = !["vectorization"].includes(asset?.metadata.type ?? "");

  const handleImageClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedImage);
      if (asset) {
        onRefineClick(asset);
      }
    },
    [onRefineClick, asset]
  );

  if (!canReuseImage) {
    return null;
  }

  return (
    <>
      {top}

      <MenuItem
        w="170px"
        iconId="Nav/Image/Outline"
        iconH="12px"
        onClick={handleImageClick}
        text="Image"
      />

      {bottom}
    </>
  );
}

// ------------------------------------

export type ListActionsCustomProps = {
  isColorMode: boolean;
  isSplineMode: boolean;
  onMoreClick: (key: string, value: any) => void;
};

export type ListActionsProps = FmImageListActionsProps<ListActionsCustomProps>;

function ListActions({
  job,
  firstAsset,
  isColorMode,
  isSplineMode,
  onMoreClick,
}: ListActionsProps) {
  const { isReader } = useTeamContext();
  const jobInput = job?.metadata.input as any;
  const colorMode = jobInput?.colorMode;
  const filterSpeckle = jobInput?.filterSpeckle;
  const colorPrecision = jobInput?.colorPrecision; // only if colorMode === "color"
  const layerDifference = jobInput?.layerDifference; // only if colorMode === "color"
  const mode = jobInput?.mode;
  const cornerThreshold = jobInput?.cornerThreshold; // only if mode === "spline"
  const lengthThreshold = jobInput?.lengthThreshold; // only if mode === "spline"
  const spliceThreshold = jobInput?.spliceThreshold; // only if mode === "spline"
  const isActionDisplayed = job?.jobType === "vectorize" && !isReader;

  const moreOptions = useMemo(() => {
    if (!isActionDisplayed) {
      return [];
    }
    return _.compact([
      colorMode !== undefined && {
        id: "colorMode",
        label: "Color Clustering",
        value: _.get(VECTORIZATION_COLOR_MODE_LABELS, [colorMode]) ?? colorMode,
        onClick: () => onMoreClick("colorMode", colorMode),
      },
      filterSpeckle !== undefined && {
        id: "filterSpeckle",
        label: "Filter Speckle",
        value: filterSpeckle,
        onClick: () => onMoreClick("filterSpeckle", filterSpeckle),
      },
      colorPrecision !== undefined && {
        id: "colorPrecision",
        label: "Color Precision",
        value: colorPrecision,
        onClick: isColorMode
          ? () => onMoreClick("colorPrecision", colorPrecision)
          : undefined,
      },
      layerDifference !== undefined && {
        id: "layerDifference",
        label: "Layer Difference",
        value: layerDifference,
        onClick: isColorMode
          ? () => onMoreClick("layerDifference", layerDifference)
          : undefined,
      },
      mode !== undefined && {
        id: "mode",
        label: "Curve Detection",
        value: _.get(VECTORIZATION_MODE_LABELS, [mode]) ?? mode,
        onClick: () => onMoreClick("mode", mode),
      },

      cornerThreshold !== undefined && {
        id: "cornerThreshold",
        label: "Corner Thresh",
        value: cornerThreshold,
        onClick: isSplineMode
          ? () => onMoreClick("cornerThreshold", cornerThreshold)
          : undefined,
      },
      lengthThreshold !== undefined && {
        id: "lengthThreshold",
        label: "Length Thresh",
        value: lengthThreshold,
        onClick: isSplineMode
          ? () => onMoreClick("lengthThreshold", lengthThreshold)
          : undefined,
      },
      spliceThreshold !== undefined && {
        id: "spliceThreshold",
        label: "Splice Thresh",
        value: spliceThreshold,
        onClick: isSplineMode
          ? () => onMoreClick("spliceThreshold", spliceThreshold)
          : undefined,
      },
    ]);
  }, [
    colorMode,
    colorPrecision,
    cornerThreshold,
    filterSpeckle,
    isColorMode,
    isSplineMode,
    layerDifference,
    lengthThreshold,
    mode,
    onMoreClick,
    spliceThreshold,
    isActionDisplayed,
  ]);

  if (!isActionDisplayed) {
    return null;
  }

  return (
    <>
      <ReuseButton job={job} firstAsset={firstAsset} />
      <MoreTooltip options={moreOptions} />
    </>
  );
}
